import { Component, EventEmitter, Inject, Output, OnInit } from '@angular/core';
import { Subject } from 'rxjs';

import { UserService } from '../../../humain/user/services/user.service';
import { EntrepriseService } from '../../../entreprises/services/entreprise.service';

import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';
import { AgenceTLR } from '../../../structure/agence/entities/agence-tlr.entity';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { GroupementTLR } from '../../../structure/groupement-parc/entities/groupement-tlr.entity';
import { PaSearchCriteria } from '../../entities/personne-avertie-search-criteria';
import { ParcWithGroupementDTO } from '../../../structure/parc/entities/parc-with-groupement-dto.entity';
import { EtatDocumentPa, TypeDocumentPa } from "../../entities/document-pa.entity";

export enum ValiditePa {
	EN_COURS = 0,
	EXPIRE = 1,
	ANNULE = 2
}

@Component({
	selector: 'documents-pa-search',
	templateUrl: 'documents-pa-search.html',
	styleUrls: ['documents-pa-search.css'],
})
export class DocumentsPaSearchComponent implements OnInit {

	user!: UserTLR;
	userRole: string = '';
	entreprises!: EntrepriseTLR[];
	searchCriteria: PaSearchCriteria = new PaSearchCriteria();

	typePaOptions = [TypeDocumentPa.SUR_SITE, TypeDocumentPa.A_DISTANCE];
	etatDocumentPa = [EtatDocumentPa.BROUILLON, EtatDocumentPa.A_FAIRE_SIGNER, EtatDocumentPa.SIGNE_PARTIELLEMENT, EtatDocumentPa.SIGNE,  EtatDocumentPa.ARCHIVER, EtatDocumentPa.ATTENTE_DE_RELECTURE, EtatDocumentPa.INVALIDE];
	validiteOptions = [ValiditePa.EN_COURS, ValiditePa.EXPIRE];

	resetCommands = new Subject<void>();

	@Output() onApplyFilters = new EventEmitter<PaSearchCriteria>();

	private typePaLabels: Record<TypeDocumentPa, string> = {
		[TypeDocumentPa.SUR_SITE]: 'Sur site',
		[TypeDocumentPa.A_DISTANCE]: 'À distance',
	};

	private etatPaLabels: Record<EtatDocumentPa, string> = {
		[EtatDocumentPa.BROUILLON]: 'Brouillon',
		[EtatDocumentPa.A_FAIRE_SIGNER]: 'À faire signer',
		[EtatDocumentPa.ENVOYE_VIA_DOCUSIGN]: 'Envoyé via DocuSign',
		[EtatDocumentPa.SIGNE_PARTIELLEMENT]: 'Signé partiellement',
		[EtatDocumentPa.SIGNE]: 'Signé',
		[EtatDocumentPa.ARCHIVER]: 'Archivé',
		[EtatDocumentPa.ATTENTE_DE_RELECTURE]: 'Attente de relecture',
		[EtatDocumentPa.INVALIDE]: 'Invalide',
	};

	private validiteLabels: string[] = ["En cours", "Expiré"];


	getTypePaLabel = (typePa: TypeDocumentPa) => this.typePaLabels[typePa];
	getEtatLabel = (etatPa: EtatDocumentPa) => this.etatPaLabels[etatPa];
	getValiditeLabel = (validitePa: ValiditePa) => this.validiteLabels[validitePa];

	typePaShouldBeSelectedByDefaut = (_typePa: TypeDocumentPa) => true;
	etatPaShouldBeSelectedByDefaut = (etatPa: EtatDocumentPa) => etatPa != EtatDocumentPa.ARCHIVER; // par défaut les archivés ne sont pas affichés
	validitePaShouldBeSelectedByDefaut = (_validitePa: ValiditePa) => true;

	getEntrepriseName = (entreprise: EntrepriseTLR) => entreprise.nom;

	constructor(
		@Inject(UserService) private userService: UserService,
		@Inject(EntrepriseService) private entrepriseService: EntrepriseService
	) { }

	ngOnInit() {
		this.initForm(false);
	}

	initForm(shouldApplyFilter: boolean) {
		this.searchCriteria = new PaSearchCriteria();
		this.userService.getCurrentUserTLR().subscribe(
			(user: UserTLR) => {
				if (user) {
					this.userRole = user.role;
					this.user = user;
					if (user.role === 'EXP' && user.antenneId) {
						this.searchCriteria.antenne = user.antenneId;
					}
					let entreprises$ = this.entrepriseService.getActiveEntreprisesTLRList();
					entreprises$.subscribe((entreprises: EntrepriseTLR[]) => {
						this.entreprises = entreprises.filter( $ => $.documentApplicable === 'PersonneAvertie' || $.documentApplicable === 'Both');
						if (shouldApplyFilter) {
							this.applyfilters();
						}
					});
				}
			}
		);
	}

	onNumeroPaChange() {
		this.applyfilters();
	}

	onPerimetreReferentChange(selected: boolean) {
		this.applyfilters();
	}

	onEntrepriseSelected(entreprise: EntrepriseTLR) {
		if (entreprise) {
			this.searchCriteria.entreprise = Number(entreprise.id);
			this.applyfilters();
		}
	}

	onTypePaSelected(value: [TypeDocumentPa[], boolean]) {
		this.searchCriteria.typePa = value[0];
		this.applyfilters();
	}

	onEtatPaSelected(value: [EtatDocumentPa[], boolean]) {
		this.searchCriteria.etat = value[0];
		this.applyfilters();
	}

	onValiditePaSelected(value: [number[], boolean]) {
		this.searchCriteria.validite = value[0];
		this.applyfilters();
	}

	onAgenceChange(selectedAgence: AgenceTLR | null) {
		if (selectedAgence) {
			if (selectedAgence.id != this.searchCriteria.agence) {
				this.searchCriteria.agence = selectedAgence.id;
				delete this.searchCriteria.antenne;
				delete this.searchCriteria.groupement;
				delete this.searchCriteria.parc;
			}
		} else {
			delete this.searchCriteria.agence;
			delete this.searchCriteria.antenne;
			delete this.searchCriteria.groupement;
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}

	onAntenneChange(selectedAntenne: AntenneTLR | null) {
		if (selectedAntenne) {
			if (this.searchCriteria.agence === undefined) {
				this.searchCriteria.agence = selectedAntenne.agenceId;
			}
			if (selectedAntenne.id != this.searchCriteria.antenne) {
				this.searchCriteria.antenne = selectedAntenne.id;
				delete this.searchCriteria.parc;
			}
		} else {
			delete this.searchCriteria.antenne;
			delete this.searchCriteria.groupement;
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}

	onGroupementChange(selectedGroupement: GroupementTLR | null) {
		if (selectedGroupement) {
			this.searchCriteria.groupement = selectedGroupement.id;
			if (this.searchCriteria.antenne === undefined) {
				this.searchCriteria.antenne = selectedGroupement.antenneId;
			}
		} else {
			delete this.searchCriteria.groupement;
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}

	onParcChange(selectedParc: ParcWithGroupementDTO | null) {
		if (selectedParc) {
			this.searchCriteria.parc = selectedParc.id;
			if (this.searchCriteria.antenne === undefined) {
				this.searchCriteria.antenne = selectedParc.antenneId;
			}
			if (this.searchCriteria.groupement === undefined) {
				this.searchCriteria.groupement = selectedParc.groupementId;
			}
		} else {
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}

	resetFilters() {

		const perimetreReferent = this.searchCriteria.monPerimetreReferent;
		const agence = this.searchCriteria.agence;
		const antenne = this.searchCriteria.antenne;

		this.searchCriteria = new PaSearchCriteria();

		if (this.userRole !== 'CCE') {
			this.searchCriteria.monPerimetreReferent = perimetreReferent;
			this.searchCriteria.agence = agence;
			this.searchCriteria.antenne = antenne;
		}

		this.resetCommands.next();
		this.applyfilters();
	}

	applyfilters() {
		this.onApplyFilters.emit(this.searchCriteria);
	}
}
